import Vue from 'vue'
import Vuex from 'vuex'
import router from '../router'
// import Firebase from 'firebase/app'
import { Functions } from '../firebase/functions'
import { Auth } from '../firebase/auth'
import { DB } from '../firebase/db'
import { parseSubscriptionLimits } from '../stripePlans'

const getStripeCustomer = Functions.httpsCallable('getStripeCustomer')
const recreateStripeCustomer = Functions.httpsCallable('recreateStripeCustomer')

// import _ from 'lodash'
// import http from '../http-common'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    authReady: false,
    user: {
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      emailVerified: false,
      photoURL: '',
      isAnonymous: false,
      uid: '',
      isAdmin: false,
      stripeAccountExists: false,
    },
    maxCustomers: undefined,
    stripeLoading: true,
    key: 0,
  },
  getters: {
    user: (state) => state.user 
  },
  mutations: {
    iterateKey(state) {
      state.key += 1
    },
    getCustomerData(state, customerData) {
      console.log('getCustomerData')
      state.stripeLoading = false
      state.maxCustomers = customerData.maxCustomers
      state.user.stripeAccountExists = customerData.stripeAccountExists
    },
    signOut(state) {
      state.user.firstName = ''
      state.user.lastName = ''
      state.user.userName = ''
      state.user.email = ''
      state.user.emailVerified = false
      state.user.photoURL = ''
      state.user.isAnonymous = false
      state.user.uid = ''
      if(router.currentRoute.name !== 'SignIn'){
        router.push({ name: 'SignIn' })
      }
    },
    loggedIn(state, userProfile) {
      state.user.firstName = userProfile.firstName
      state.user.lastName = userProfile.lastName
      state.user.userName = userProfile.userName
      state.user.email = userProfile.email
      state.user.emailVerified = Auth.currentUser.emailVerified
      state.user.photoURL = userProfile.photoURL
      state.user.isAnonymous = userProfile.isAnonymous
      state.user.uid = userProfile.uid
      state.user.isAdmin = userProfile.isAdmin
      // Only redirect when on a login page; don't redirect when on Home (so refresh on main page stays on main page)
      const routeName = router.currentRoute.name
      const onLoginPage = routeName === 'SignIn' || routeName === 'UserLogin'
      if (onLoginPage) {
        if (state.user.isAdmin) {
          router.push({ name: 'AdminLanding' })
        } else if (state.user.email) {
          router.push({ name: 'TenantLanding' })
        }
      }
    },
    registerAccount(state, userProfile) {
      state.user.firstName = userProfile.firstName
      state.user.lastName = userProfile.lastName
      state.user.userName = userProfile.userName
      state.user.email = userProfile.email
      state.user.emailVerified = Auth.currentUser.emailVerified
      state.user.photoURL = userProfile.photoURL
      state.user.isAnonymous = userProfile.isAnonymous
      state.user.uid = userProfile.uid
      state.user.isAdmin = userProfile.isAdmin
      // After register we're on SignIn/GetStarted flow; redirect to tenant landing
      if (router.currentRoute.name !== 'TenantLanding') {
        router.push({ name: 'TenantLanding' })
      }
    },
    loginAccount(state, userProfile) {
      state.user.firstName = userProfile.firstName
      state.user.lastName = userProfile.lastName
      state.user.userName = userProfile.userName
      state.user.email = userProfile.email
      state.user.emailVerified = Auth.currentUser.emailVerified
      state.user.photoURL = userProfile.photoURL
      state.user.isAnonymous = userProfile.isAnonymous
      state.user.uid = userProfile.uid
      state.user.isAdmin = userProfile.isAdmin
      // After explicit login, send to app landing (condition was previously always true due to ||)
      const routeName = router.currentRoute.name
      if (routeName !== 'AdminLanding' && routeName !== 'TenantLanding') {
        if (userProfile.isAdmin) {
          router.push({ name: 'AdminLanding' })
        } else {
          router.push({ name: 'TenantLanding' })
        }
      }
    },
  },
  actions: {
    signOut({ commit }) {
      Auth.signOut()
        .then(function() {
          commit('signOut')
        })
        .catch(function(err) {
          alert(err.message)
        })
    },
    recreateStripeCustomer: ({ commit }, payload) => {
      recreateStripeCustomer(payload).then((customer) => {
        const subscriptions = customer.data.subscriptions ? customer.data.subscriptions.data : undefined
        commit('getCustomerData', parseSubscriptionLimits(subscriptions))

      }).catch((err) => {
        console.error('recreateStripeCustomer failed:', err)
        commit('getCustomerData', { maxCustomers: 0, stripeAccountExists: false })
      })
    },
    getCustomerData: ({ commit, dispatch }, payload) => {
      getStripeCustomer({ stripeCustomerId: payload.stripeCustomerId }).then((customer) => {
        const subscriptions = customer.data.subscriptions ? customer.data.subscriptions.data : undefined
        commit('getCustomerData', parseSubscriptionLimits(subscriptions))
      }).catch((err) => {
        console.error('getStripeCustomer failed:', err)
        if (payload.email && payload.uid) {
          dispatch('recreateStripeCustomer', { email: payload.email, uid: payload.uid })
        } else {
          commit('getCustomerData', { maxCustomers: 0, stripeAccountExists: false })
        }
      })
    },
    waitForAuth({ state }) {
      if (state.authReady) return Promise.resolve()
      return new Promise(resolve => {
        const timeout = setTimeout(() => {
          state.authReady = true
          resolve()
        }, 5000)
        const check = () => {
          if (state.authReady) {
            clearTimeout(timeout)
            resolve()
          } else {
            setTimeout(check, 50)
          }
        }
        check()
      })
    },
    loggedIn: async ({ commit, dispatch, state }) => {
      Auth.onAuthStateChanged((user) => {
        console.log('Vuex loggedIn')
        if (user) {
          user.getIdTokenResult().then(idTokenResult => {
            let userDocRef = DB.collection('user').doc(user.uid)
            let stripeDocRef = DB.collection('user').doc(user.uid).collection('stripe').doc('stripe_customer')

            userDocRef.get().then((userDoc) => {
              const userProfile = userDoc.data()
              userProfile.isAdmin = idTokenResult.claims.admin
              commit('loggedIn', userProfile)
              if (!state.authReady) state.authReady = true

              const stripePayload = {
                email: userProfile.email,
                uid: userProfile.uid,
              }

              stripeDocRef.get().then((stripeInfo) => {
                const stripeCustomerId = stripeInfo.exists && stripeInfo.data()
                  ? stripeInfo.data().stripe_customer_id
                  : null

                if (!stripeCustomerId) {
                  dispatch('recreateStripeCustomer', stripePayload)
                  return
                }

                dispatch('getCustomerData', {
                  stripeCustomerId,
                  ...stripePayload,
                })
              }).catch((err) => {
                console.error('stripe doc fetch failed:', err)
                dispatch('recreateStripeCustomer', stripePayload)
              })
            })
          })
        } else {
          if (!state.authReady) state.authReady = true
        }
      })
    },
    registerAccount({ commit }, payload) {
      Auth.createUserWithEmailAndPassword(payload.email, payload.password)
        .then((user) => {
          let userProfile = {
            firstName: payload.firstName,
            lastName: payload.lastName,
            userName: payload.userName,
            email: payload.email,
            uid: user.user.uid,
            emailVerified: user.user.emailVerified,
            photoURL: user.user.photoURL,
            isAnonymous: user.user.isAnonymous,
          }
          DB.collection('user')
            .doc(user.user.uid)
            .set({
              firstName: userProfile.firstName,
              lastName: userProfile.lastName,
              userName: userProfile.userName,
              email: userProfile.email,
              uid: userProfile.uid,
              emailVerified: userProfile.emailVerified,
              photoURL: userProfile.photoURL,
              isAnonymous: userProfile.isAnonymous,
            })
            .then(function() {
              console.log('Document successfully written!')
            })
            .catch(function(error) {
              console.error('Error writing document: hello', error)
            })
          commit('registerAccount', userProfile)
        })
        .catch(function(err) {
          alert(err.message)
        })
    },
    sendEmailVerification() {
      let user = Auth.currentUser
      const continueUrl = typeof window !== 'undefined' ? `${window.location.origin}/email-verified` : 'https://app.certainfee.com/email-verified'
      user.sendEmailVerification({
        url: continueUrl,
        handleCodeInApp: false
      })
        .then(function() {
          alert('Verification Email Sent\n\nIf you don\'t receive the email link soon,\nplease check your spam folder.')
        })
        .catch(function(error) {
          alert(error)
        })
    },
    loginAccount({ commit }, payload) {
      Auth.signInWithEmailAndPassword(payload.email, payload.password).then(
        (user) => {
          let uid = user.user.uid
          user.user.getIdTokenResult().then(idTokenResult => {
            DB.collection('user')
              .doc(uid)
              .get()
              .then((user) => {
                if ('idTokenResult.claims.admin && !user.isAdmin: ', idTokenResult.claims.admin && !user.isAdmin) {
                  DB.collection('user')
                    .doc(uid)
                    .set({isAdmin: true}, {merge: true})
                }
                user = user.data()
                let userProfile = {
                  firstName: user.firstName,
                  lastName: user.lastName,
                  userName: user.userName,
                  email: user.email,
                  uid: user.uid,
                  emailVerified: user.emailVerified,
                  photoURL: user.photoURL,
                  isAnonymous: user.isAnonymous,
                  isAdmin: idTokenResult.claims.admin,
                }
                commit('loginAccount', userProfile)
              })
            })
        })
        .catch(function(err) {
          alert(err.message)
        })
    },
    iterateKey({ commit }) {
      commit('iterateKey')
    }
  },
  modules: {
  }
})
